import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { db } from "@/lib/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, User, Building2, Briefcase, Users } from "lucide-react";

interface UserRole {
  role: 'buyer' | 'seller' | 'broker' | 'consultant';
  subscription?: 'byod' | 'essential' | 'essential_plus' | 'pro';
  isActive: boolean;
}

export function RoleSwitcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [currentRole, setCurrentRole] = useState<string>('buyer');

  useEffect(() => {
    if (user) {
      loadUserRoles();
    }
  }, [user]);

  const loadUserRoles = async () => {
    if (!user) return;

    try {
      const userDoc = await getDoc(doc(db, "users", user.id));
      const userData = userDoc.data();

      if (userData?.roles) {
        setUserRoles(userData.roles);
        setCurrentRole(userData.activeRole || 'buyer');
      }
    } catch (error) {
      console.error("Error loading user roles:", error);
    }
  };

  const switchRole = async (role: string) => {
    setCurrentRole(role);

    // Navigate to appropriate dashboard
    switch (role) {
      case 'buyer':
        navigate('/buyer/dashboard');
        break;
      case 'seller':
        navigate('/seller/dashboard');
        break;
      case 'broker':
        navigate('/dashboard/broker');
        break;
      case 'consultant':
        navigate('/dashboard/consultant');
        break;
    }

    // Update active role in database
    if (user) {
      try {
        const userRef = doc(db, "users", user.id);
        await updateDoc(userRef, { activeRole: role });
      } catch (error) {
        console.error("Error updating active role:", error);
      }
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'buyer':
        return <User className="h-4 w-4" />;
      case 'seller':
        return <Building2 className="h-4 w-4" />;
      case 'broker':
        return <Briefcase className="h-4 w-4" />;
      case 'consultant':
        return <Users className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleLabel = (role: string) => {
    if (!role) return 'Unknown';
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  const getSubscriptionBadge = (subscription?: string) => {
    if (!subscription) return null;

    const colors: Record<string, string> = {
      'byod': 'bg-gray-500',
      'essential': 'bg-blue-500',
      'essential_plus': 'bg-purple-500',
      'pro': 'bg-amber-500'
    };

    return (
      <Badge className={`${colors[subscription] || 'bg-gray-500'} ml-2 text-xs`}>
        {subscription.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  // Don't show switcher if user has only one role or no roles
  if (userRoles.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          {getRoleIcon(currentRole)}
          {getRoleLabel(currentRole)}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Switch Role</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {userRoles.filter(roleData => roleData && roleData.role).map((roleData) => (
          <DropdownMenuItem
            key={roleData.role}
            onClick={() => switchRole(roleData.role)}
            className={currentRole === roleData.role ? 'bg-muted' : ''}
          >
            <div className="flex items-center w-full">
              {getRoleIcon(roleData.role)}
              <span className="ml-2">{getRoleLabel(roleData.role)}</span>
              {getSubscriptionBadge(roleData.subscription)}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
